/**
 * HighlightRenderer Component
 *
 * Applies highlights to rendered content using CSS Custom Highlight API.
 * Works as a wrapper component that doesn't change the rendering of its children.
 */

import React, { useEffect, useRef } from 'react';

/**
 * Check if browser supports CSS Custom Highlight API
 */
const supportsHighlightAPI = () => {
  return typeof CSS !== 'undefined' && CSS.highlights !== undefined;
};

/**
 * Find text node and offset for a character position
 * @param {HTMLElement} container - Container element
 * @param {number} targetOffset - Target character offset
 * @returns {Object|null} Object with node and offset, or null
 */
const findTextNodeAtOffset = (container, targetOffset) => {
  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_TEXT,
    null,
    false
  );

  let currentOffset = 0;
  let node;

  while ((node = walker.nextNode())) {
    const nodeLength = node.textContent.length;

    if (currentOffset + nodeLength >= targetOffset) {
      return {
        node,
        offset: targetOffset - currentOffset
      };
    }

    currentOffset += nodeLength;
  }

  return null;
};

/**
 * Apply highlights using CSS Custom Highlight API
 * @param {Array} highlights - Array of highlight objects
 * @param {HTMLElement} container - Container element
 * @param {string} renderedText - Rendered text from DOM
 */
const applyHighlightsWithAPI = (highlights, container, renderedText) => {
  if (!supportsHighlightAPI() || !container || !renderedText) return;

  // Clear existing highlights
  CSS.highlights.clear();

  highlights.forEach((highlight, index) => {
    try {
      // Get both selectors for robust position finding
      const textPosSelector = highlight.annotation?.target?.selector?.find(
        (s) => s.type === 'TextPositionSelector'
      );
      const textQuoteSelector = highlight.annotation?.target?.selector?.find(
        (s) => s.type === 'TextQuoteSelector'
      );

      if (!textPosSelector || !textQuoteSelector) return;

      let { start, end } = textPosSelector;
      const { exact: exactText } = textQuoteSelector;

      // console.log('[HighlightRenderer] Applying highlight', index, 'initial positions:', start, '-', end);
      // console.log('[HighlightRenderer] Exact text length:', exactText?.length);
      // console.log('[HighlightRenderer] Stored position length:', end - start);

      // Check if stored positions match the exact text
      const storedText = renderedText.substring(start, end);
      const matches = storedText === exactText;
      // console.log('[HighlightRenderer] Stored positions match exact text?', matches);

      // If positions don't match, re-search using the exact text with context
      if (!matches && exactText) {
        // console.log('[HighlightRenderer] Re-searching for correct positions...');

        // Normalize both texts for comparison (handle newlines vs spaces)
        const normalizeForSearch = (text) => text
          .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold markers (**text** -> text)
          .replace(/）\s+(\d+\.)/g, '）$1') // Remove space/newline between Chinese paren and numbered list: ）\n1. -> ）1.
          .replace(/。\s+(\d+\.)/g, '。$1') // Remove space/newline between period and numbered list: 。\n2. -> 。2.
          .replace(/\s*[-*+]\s+/g, ' ')
          .replace(/\s*\d+\.\s+/g, ' ') // Remove numbered list markers (1. 2. 3. etc.)
          .replace(/\s+/g, ' ')
          .replace(/\s*•/g, '•')
          .trim();

        const normalizedRendered = normalizeForSearch(renderedText);
        const normalizedExact = normalizeForSearch(exactText);

        // Find in normalized text
        const normalizedStart = normalizedRendered.indexOf(normalizedExact);

        if (normalizedStart !== -1) {
          // Find actual positions by searching for first/last few characters
          const searchStart = exactText.trim().substring(0, 15);
          const searchEnd = exactText.trim().slice(-15);

          const actualStart = renderedText.indexOf(searchStart);
          if (actualStart !== -1) {
            const actualEndMatch = renderedText.indexOf(searchEnd, actualStart + 10);
            if (actualEndMatch !== -1) {
              start = actualStart;
              end = actualEndMatch + searchEnd.length;
              // console.log('[HighlightRenderer] ✅ Re-found positions:', start, '-', end);
              // console.log('[HighlightRenderer] New text:', renderedText.substring(start, end).substring(0, 50) + '...');
            }
          }
        }
      }

      // Find the actual text nodes in the rendered DOM
      const startNodeInfo = findTextNodeAtOffset(container, start);
      const endNodeInfo = findTextNodeAtOffset(container, end);

      if (!startNodeInfo || !endNodeInfo) {
        console.warn(`Could not find text nodes for highlight ${highlight.id}`);
        return;
      }

      // Create a Range for the highlight
      const range = document.createRange();
      range.setStart(startNodeInfo.node, startNodeInfo.offset);
      range.setEnd(endNodeInfo.node, endNodeInfo.offset);

      // Create and register the highlight using the CSS Highlight API
      // Use array index instead of database ID to keep IDs small and article-scoped
      // This ensures we never exceed the CSS limit and can reuse CSS across articles
      // eslint-disable-next-line no-undef
      const highlightObj = new Highlight(range);
      CSS.highlights.set(`highlight-${index}`, highlightObj);
    } catch (error) {
      console.warn(`Failed to apply highlight ${highlight.id}:`, error);
    }
  });
};

/**
 * HighlightRenderer Component
 * Wraps content and applies highlights on top using CSS
 */
const HighlightRenderer = ({ highlights = [], onHighlightClick, children }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (supportsHighlightAPI() && containerRef.current && highlights.length > 0) {
      // Extract text from the rendered DOM itself
      const renderedText = containerRef.current.textContent;
      applyHighlightsWithAPI(highlights, containerRef.current, renderedText);
    }

    // Cleanup
    return () => {
      if (supportsHighlightAPI()) {
        CSS.highlights.clear();
      }
    };
  }, [highlights]); // Only re-run when highlights change, not when children change

  // Handle highlight clicks (would need to detect which highlight was clicked)
  const handleClick = () => {
    if (!onHighlightClick) return;

    // This is complex - we'd need to determine which highlight was clicked
    // Future enhancement: detect which specific highlight was clicked
  };

  return (
    <div
      ref={containerRef}
      className="highlight-container"
      onClick={handleClick}
    >
      {children}
    </div>
  );
};

export default HighlightRenderer;
